import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import AppShell from '../components/work/AppShell';
import ServiceRequestDetail from '../components/work/ServiceRequestDetail';
import { LoadingRows, WorkPageError } from '../components/work/page-states';
import { apiFetch, v1Path } from '../lib/api';
import type { ServiceRequest } from '../lib/service-requests';
import { serviceRequestBackTarget } from '../lib/service-request-views';
import type { StateDto } from '../lib/work-types';

export default function ServiceRequestPage() {
  const { projectId, workItemId } = useParams<{ projectId: string; workItemId: string }>();
  const back = serviceRequestBackTarget(useLocation().state);
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [states, setStates] = useState<StateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId || !workItemId) return;
    setLoading(true);
    setError(null);
    try {
      const [next, statePage] = await Promise.all([
        apiFetch<ServiceRequest>(v1Path(`/projects/${projectId}/service-requests/${workItemId}`)),
        apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)),
      ]);
      setRequest(next);
      setStates(statePage.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Service Request');
    } finally {
      setLoading(false);
    }
  }, [projectId, workItemId]);

  useEffect(() => { void load(); }, [load]);

  if (!projectId || !workItemId) return null;
  return (
    <AppShell projectId={projectId}>
      <div className="max-w-6xl">
        <Link to={back.path} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
        </Link>
        {loading ? <LoadingRows /> : null}
        <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
        {request ? <ServiceRequestDetail projectId={projectId} request={request} states={states} onChanged={setRequest} /> : null}
      </div>
    </AppShell>
  );
}
